import updateNotesSource from "./updateNotes.json";

export const UPDATE_CONTENT_LANGUAGES = ["en", "ko", "ja"] as const;

export type UpdateContentLanguage =
  (typeof UPDATE_CONTENT_LANGUAGES)[number];

export type UpdateSection = {
  heading: string;
  items: string[];
};

export type UpdateTranslation = {
  title: string;
  summary: string;
  lead: string;
  sections: UpdateSection[];
};

export type UpdateNote = {
  slug: string;
  date: string;
  updated: string;
  version?: string;
  translations: Record<UpdateContentLanguage, UpdateTranslation>;
};

type UpdatePageLabels = {
  eyebrow: string;
  title: string;
  lead: string;
  fallback: string;
  latest: string;
  version: string;
  details: string;
  listTitle: string;
  listLead: string;
  page: string;
  previous: string;
  next: string;
  download: string;
};

const labels: Record<UpdateContentLanguage, UpdatePageLabels> = {
  en: {
    eyebrow: "AUTOVID · PRODUCT UPDATES",
    title: "AUTOVID updates",
    lead: "See what changed in AUTOVID, from new production workflows to editing and publishing improvements.",
    fallback: "Update notes are currently shown in English for this language.",
    latest: "Latest update",
    version: "Version",
    details: "Update details",
    listTitle: "Previous updates",
    listLead: "Choose an update to replace the details shown above.",
    page: "Page",
    previous: "Previous",
    next: "Next",
    download: "Download AUTOVID",
  },
  ko: {
    eyebrow: "AUTOVID · 제품 업데이트",
    title: "AUTOVID 업데이트",
    lead: "새로운 제작 방식부터 편집과 게시 개선까지, AUTOVID에 추가된 내용을 확인하세요.",
    fallback: "이 언어의 업데이트 노트는 현재 영어로 제공됩니다.",
    latest: "최신 업데이트",
    version: "버전",
    details: "업데이트 상세 내용",
    listTitle: "지난 업데이트",
    listLead: "업데이트를 선택하면 위 상세 내용이 해당 기록으로 바뀝니다.",
    page: "페이지",
    previous: "이전",
    next: "다음",
    download: "AUTOVID 다운로드",
  },
  ja: {
    eyebrow: "AUTOVID · 製品アップデート",
    title: "AUTOVIDアップデート",
    lead: "新しい制作フローから編集・公開機能の改善まで、AUTOVIDの変更内容をご案内します。",
    fallback: "この言語のアップデート情報は現在英語で表示されます。",
    latest: "最新アップデート",
    version: "バージョン",
    details: "アップデート内容",
    listTitle: "過去のアップデート",
    listLead: "アップデートを選択すると、上の詳細内容が切り替わります。",
    page: "ページ",
    previous: "前へ",
    next: "次へ",
    download: "AUTOVIDをダウンロード",
  },
};

export const updateNotes = (updateNotesSource.updates as UpdateNote[])
  .slice()
  .sort((left, right) => right.date.localeCompare(left.date));

export const isPrimaryUpdateLanguage = (language: string): boolean => {
  const normalized = language.trim().toLowerCase().split("-")[0];
  return UPDATE_CONTENT_LANGUAGES.includes(
    normalized as UpdateContentLanguage,
  );
};

export const getUpdateContentLanguage = (
  language: string,
): UpdateContentLanguage => {
  const normalized = language.trim().toLowerCase().split("-")[0];
  if (normalized === "ko" || normalized === "ja") {
    return normalized;
  }

  return "en";
};

export const getUpdateLabels = (language: string): UpdatePageLabels =>
  labels[getUpdateContentLanguage(language)];

export const getUpdateNote = (slug?: string): UpdateNote | undefined => {
  if (!slug) {
    return updateNotes[0];
  }

  return updateNotes.find((update) => update.slug === slug);
};

export const formatUpdateDate = (
  date: string,
  language: UpdateContentLanguage,
): string => {
  const locale = language === "ko" ? "ko-KR" : language === "ja" ? "ja-JP" : "en-US";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
};
